import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useEffect } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";
import {
	LayoutAlignBottom,
	LayoutAlignTop,
} from "../../../components/icons/Icons";
import SidebarDrawer from "../../../components/sidebar-drawer/SidebarDrawer";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
	postTypeOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			layout,

			postType,

			postCount,
			category,
			tag,
			orderBy,
			orderType,
			excludedCategory,
			authorName,
			offset,

			postTitleTypography,
			postTitleColor,
			postTitleHoverColor,
			tabBackground,
			tabTypography,
			tabColor,

			tabHeaderBorder,
			tabHeaderBoxShadow,

			titleMarkup,
			blockPadding,
			blockMargin,

			enableCategory,
			enableExcerpt,
			excerptTypography,
			excerptColor,
			categoryColor,
			categoryBackground,
			categoryHoverColor,
			categoryHoverBackground,
			categoryPadding,
			enableCategoryBorder,
			categoryBorder,
			categoryBoxShadow,
			categoryHoverBorder,
			categoryBoxShadowHover,

			metaPosition,
			enableAuthor,
			enableDate,
			metaIconColor,
			metaLinkColor,
			metaLinkHoverColor,
			metaTypography,
			metaSeparatorColor,

			enableMetaSeparator,
			separatorType,
			enableIcon,
			imageHeight,
			imageWidth,
			objectFit,

			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
		},
		setAttributes,
		categories,
		tags,
		authorOptions,
		postTypeOptions,
	} = props;

	useEffect(() => {
		const type = wp.data.select("core/editor").getCurrentPost().type;

		if (type === "mzb-builder-template") {
			setAttributes({ postType: "mzb-builder" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Post Title", "magazine-blocks")} initialOpen>
					<Select
						options={[
							{
								label: __("H1", "magazine-blocks"),
								value: "h1",
							},
							{
								label: __("H2", "magazine-blocks"),
								value: "h2",
							},
							{
								label: __("H3", "magazine-blocks"),
								value: "h3",
							},
							{
								label: __("H4", "magazine-blocks"),
								value: "h4",
							},
							{
								label: __("H5", "magazine-blocks"),
								value: "h5",
							},
							{
								label: __("H6", "magazine-blocks"),
								value: "h6",
							},
						]}
						onChange={(val) => setAttributes({ titleMarkup: val })}
						value={titleMarkup}
						label={__("HTML Markup", "magazine-blocks")}
						// @ts-ignore
						inline={false}
					/>
				</Panel>
				<Panel title={__("Tab Header", "magazine-blocks")}>
					<Select
						options={[
							{
								label: __("Default", "magazine-blocks"),
								value: "default",
							},
							{
								label: __("Style 1", "magazine-blocks"),
								value: "style1",
							},
						]}
						onChange={(val) => setAttributes({ layout: val })}
						value={layout}
						label={__("Presets", "magazine-blocks")}
						// @ts-ignore
						inline={false}
					/>
				</Panel>
				<Panel title={__("Query", "magazine-blocks")}>
					{postType !== "mzb-builder" && (
						<>
							<Select
								value={category}
								onChange={(val) =>
									setAttributes({ category: val })
								}
								label={__("Category", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
							/>
							<Select
								value={tag}
								onChange={(val) => setAttributes({ tag: val })}
								label={__("Tag", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(tags ?? [])?.map(({ name, id }) => ({
										label: name,
										value: id.toString(),
									})),
								]}
							/>
						</>
					)}
					<Select
						options={[
							{
								label: __("Date", "magazine-blocks"),
								value: "date",
							},
							{
								label: __("Title", "magazine-blocks"),
								value: "title",
							},
							{
								label: __("Random", "magazine-blocks"),
								value: "rand",
							},
						]}
						onChange={(val) => setAttributes({ orderBy: val })}
						value={orderBy}
						label={__("Order By", "magazine-blocks")}
					/>
					<ToggleButtonGroup
						value={orderType}
						onChange={(v) => {
							setAttributes({ orderType: v });
						}}
						label={__("Order", "magazine-blocks")}
						resetAttributeKey="orderType"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									color: "#1A202C",
									svg: {
										w: 6,
										h: 6,
									},
									"&[data-active]": {
										color: "#fff !important",
									},
								},
							},
						}}
					>
						<ToggleButton value="asc">Ascending</ToggleButton>
						<ToggleButton value="desc">Descending</ToggleButton>
					</ToggleButtonGroup>
					<Select
						isMulti
						value={excludedCategory}
						onChange={(val) =>
							setAttributes({ excludedCategory: val })
						}
						label={__("Excluded Category", "magazine-blocks")}
						options={[
							...(categories ?? [])?.map(({ name, id }) => ({
								label: name,
								value: id.toString(),
							})),
						]}
					/>
					<Select
						options={authorOptions ?? []}
						onChange={(val) => setAttributes({ authorName: val })}
						value={authorName}
						label={__("Authors", "magazine-blocks")}
					/>
					<Select
						options={postTypeOptions ?? []}
						onChange={(val) => setAttributes({ postType: val })}
						value={postType}
						label={__("Post Type", "magazine-blocks")}
					/>
					<Slider
						label={__("Offset", "magazine-blocks")}
						value={offset}
						step={1}
						min={0}
						max={15}
						onChange={(val) => setAttributes({ offset: val })}
					/>
					<Slider
						label={__("Post Count", "magazine-blocks")}
						value={postCount}
						step={1}
						min={1}
						max={15}
						onChange={(val) => setAttributes({ postCount: val })}
					/>
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<Toggle
						checked={enableCategory}
						onChange={() =>
							setAttributes({
								enableCategory: !enableCategory,
							})
						}
						label={__("Category", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					<ToggleButtonGroup
						value={metaPosition}
						onChange={(v) => {
							setAttributes({ metaPosition: v });
						}}
						label={__("Position", "magazine-blocks")}
						resetAttributeKey="metaPosition"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="top">
							<LayoutAlignTop />
						</ToggleButton>
						<ToggleButton value="bottom">
							<LayoutAlignBottom />
						</ToggleButton>
					</ToggleButtonGroup>
					<Toggle
						checked={enableAuthor}
						onChange={() =>
							setAttributes({
								enableAuthor: !enableAuthor,
							})
						}
						label={__("Author", "magazine-blocks")}
					/>
					<Toggle
						checked={enableDate}
						onChange={() =>
							setAttributes({ enableDate: !enableDate })
						}
						label={__("Date", "magazine-blocks")}
					/>
					<Toggle
						checked={enableMetaSeparator}
						onChange={() =>
							setAttributes({
								enableMetaSeparator: !enableMetaSeparator,
							})
						}
						label={__("Separator", "magazine-blocks")}
					/>
					{enableMetaSeparator && (
						<Select
							options={[
								{
									label: __("None", "magazine-blocks"),
									value: "none",
								},
								{
									label: __("Circle", "magazine-blocks"),
									value: "circle",
								},
								{
									label: __("Square", "magazine-blocks"),
									value: "square",
								},
								{
									label: __(
										"Vertical Dash",
										"magazine-blocks"
									),
									value: "vertical-dash",
								},
								{
									label: __(
										"Horizontal Dash",
										"magazine-blocks"
									),
									value: "horizontal-dash",
								},
								{
									label: __(
										"Outline Circle",
										"magazine-blocks"
									),
									value: "outline-circle",
								},
							]}
							onChange={(val) =>
								setAttributes({ separatorType: val })
							}
							value={separatorType}
							label={__("Separator Type", "magazine-blocks")}
						/>
					)}
					<Toggle
						checked={enableIcon}
						onChange={() =>
							setAttributes({ enableIcon: !enableIcon })
						}
						label={__("Icon", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Excerpt", "magazine-blocks")}>
					<Toggle
						checked={enableExcerpt}
						onChange={() =>
							setAttributes({
								enableExcerpt: !enableExcerpt,
							})
						}
						label={__("Excerpt", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Image", "magazine-blocks")}>
					<Slider
						onChange={(val: any) =>
							setAttributes({ imageWidth: val })
						}
						label={__("Width", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={400}
						value={imageWidth}
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ imageHeight: val })
						}
						label={__("Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={0}
						max={400}
						value={imageHeight}
					/>
					<Select
						options={[
							{
								label: __("Contain", "magazine-blocks"),
								value: "contain",
							},
							{
								label: __("Cover", "magazine-blocks"),
								value: "cover",
							},
							{
								label: __("Fill", "magazine-blocks"),
								value: "fill",
							},
							{
								label: __("None", "magazine-blocks"),
								value: "none",
							},
							{
								label: __("Fill", "magazine-blocks"),
								value: "fill",
							},
							{
								label: __("Scale Down", "magazine-blocks"),
								value: "scale-down",
							},
							{
								label: __("Initial", "magazine-blocks"),
								value: "initial",
							},
							{
								label: __("Revert", "magazine-blocks"),
								value: "revert",
							},
							{
								label: __("Revert Layer", "magazine-blocks"),
								value: "revert-layer",
							},
							{
								label: __("Unset", "magazine-blocks"),
								value: "unset",
							},
						]}
						onChange={(val) => setAttributes({ objectFit: val })}
						value={objectFit}
						label={__("Object Fit", "magazine-blocks")}
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Tab", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: tabColor,
											id: "tabColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={tabColor}
										resetKey="tabColor"
										onReset={(v) => {
											setAttributes({
												tabColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={tabColor}
										onChange={(v) =>
											setAttributes({ tabColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={tabBackground}
										resetKey="tabBackground"
										onReset={(v) => {
											setAttributes({
												tabBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={tabBackground}
										onChange={(v) =>
											setAttributes({
												tabBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={tabTypography}
						onChange={(val) =>
							setAttributes({ tabTypography: val })
						}
					/>
				</Panel>
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Typography
						value={postTitleTypography}
						onChange={(val) =>
							setAttributes({ postTitleTypography: val })
						}
					/>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: postTitleColor,
											id: "postTitleColor",
										},
										{
											color: postTitleHoverColor,
											id: "postTitleHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={postTitleColor}
										resetKey="postTitleColor"
										onReset={(v) => {
											setAttributes({
												postTitleColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={postTitleColor}
										onChange={(v) =>
											setAttributes({ postTitleColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={postTitleHoverColor}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												postTitleHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={postTitleHoverColor}
										onChange={(v) =>
											setAttributes({
												postTitleHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Tab Header", "magazine-blocks")}>
					<Border
						value={tabHeaderBorder}
						onChange={(v) => {
							setAttributes({
								tabHeaderBorder: v,
							});
						}}
						resetAttributeKey="tabHeaderBorder"
					/>
					<BoxShadow
						resetAttributeKey="tabHeaderBoxShadow"
						value={tabHeaderBoxShadow}
						onChange={(val) =>
							setAttributes({
								tabHeaderBoxShadow: val,
							})
						}
					/>
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: categoryColor,
											id: "categoryColor",
										},
										{
											color: categoryHoverColor,
											id: "categoryHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryColor}
										resetKey="categoryColor"
										onReset={(v) => {
											setAttributes({ categoryColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryColor}
										onChange={(v) =>
											setAttributes({ categoryColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={categoryHoverColor}
										resetKey="categoryHoverColor"
										onReset={(v) => {
											setAttributes({
												categoryHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryHoverColor}
										onChange={(v) =>
											setAttributes({
												categoryHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryBackground}
										resetKey="categoryBackground"
										onReset={(v) => {
											setAttributes({
												categoryBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={categoryBackground}
										onChange={(v) =>
											setAttributes({
												categoryBackground: v,
											})
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={categoryHoverBackground}
										resetKey="categoryHoverBackground"
										onReset={(v) => {
											setAttributes({
												categoryHoverBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={categoryHoverBackground}
										onChange={(v) =>
											setAttributes({
												categoryHoverBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Dimensions
						value={categoryPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ categoryPadding: val })
						}
					/>
					<Toggle
						checked={enableCategoryBorder}
						onChange={() =>
							setAttributes({
								enableCategoryBorder: !enableCategoryBorder,
							})
						}
						label={__("Border", "magazine-blocks")}
					/>
					{enableCategoryBorder && (
						<Tabs isFitted variant="unstyled">
							<TabList
								border="1px solid #E0E0E0"
								p="4px"
								borderRadius="2px"
							>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Normal
								</Tab>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Hover
								</Tab>
							</TabList>
							<TabPanels>
								<TabPanel p="16px 0">
									<Border
										value={categoryBorder}
										onChange={(v) => {
											setAttributes({
												categoryBorder: v,
											});
										}}
									/>
									<BoxShadow
										resetAttributeKey="categoryBoxShadow"
										value={categoryBoxShadow}
										onChange={(val) =>
											setAttributes({
												categoryBoxShadow: val,
											})
										}
									/>
								</TabPanel>
								<TabPanel p="16px 0">
									<Border
										value={categoryHoverBorder}
										onChange={(v) => {
											setAttributes({
												categoryHoverBorder: v,
											});
										}}
									/>
									<BoxShadow
										resetAttributeKey="categoryBoxShadowHover"
										value={categoryBoxShadowHover}
										onChange={(val) =>
											setAttributes({
												categoryBoxShadowHover: val,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					)}
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: metaIconColor,
											id: "metaIconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaIconColor}
										resetKey="metaIconColor"
										onReset={(v) => {
											setAttributes({ metaIconColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaIconColor}
										onChange={(v) =>
											setAttributes({ metaIconColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Link Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: metaLinkColor, id: "color" },
										{
											color: metaLinkHoverColor,
											id: "hoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaLinkColor}
										resetKey="metaLinkColor"
										onReset={(v) => {
											setAttributes({ metaLinkColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkColor}
										onChange={(v) =>
											setAttributes({ metaLinkColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={metaLinkHoverColor}
										resetKey="metaLinkHoverColor"
										onReset={(v) => {
											setAttributes({
												metaLinkHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkHoverColor}
										onChange={(v) =>
											setAttributes({
												metaLinkHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Separator Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: metaSeparatorColor,
											id: "metaSeparatorColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaSeparatorColor}
										resetKey="metaSeparatorColor"
										onReset={(v) => {
											setAttributes({
												metaSeparatorColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaSeparatorColor}
										onChange={(v) =>
											setAttributes({
												metaSeparatorColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={metaTypography}
						onChange={(val) =>
							setAttributes({
								metaTypography: val,
							})
						}
					/>
				</Panel>
				<Panel title={__("Excerpt", "magazine-blocks")}>
					<Typography
						value={excerptTypography}
						onChange={(val) =>
							setAttributes({ excerptTypography: val })
						}
					/>
					<PopoverDrawer
						label={__("Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: excerptColor,
											id: "excerptColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={excerptColor}
										resetKey="excerptColor"
										onReset={(v) => {
											setAttributes({ excerptColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={excerptColor}
										onChange={(v) =>
											setAttributes({ excerptColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
